import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Upload, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";

const SAMPLE = `# Paste one wrestler per line. Fields are separated by | (pipe).
# Aliases inside a field are separated by ; (semicolon). Missing fields are OK.
#
# Format:
# primary_name | former_names | nicknames | tag_teams | factions | era
#
# Example:
Stone Cold Steve Austin | Stunning Steve Austin; The Ringmaster | The Rattlesnake; Stone Cold | The Hollywood Blondes | The Alliance | Attitude
The Rock | Rocky Maivia; Flex Kavana | The People's Champion; The Great One | The Rock 'n' Sock Connection | The Nation of Domination | Attitude
Hulk Hogan | Terry Bollea; Sterling Golden | The Hulkster; Hollywood Hogan | The Mega Powers | nWo | Golden`;

/** Parse the bulk paste sheet into wrestler rows. */
function parseSheet(text) {
  const rows = [];
  const errors = [];
  const lines = text.split(/\r?\n/);
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line || line.startsWith("#")) return;
    const parts = line.split("|").map((p) => p.trim());
    const primary = parts[0] || "";
    if (!primary) { errors.push({ line: i + 1, msg: "missing primary name" }); return; }
    const listAt = (idx) =>
      (parts[idx] || "").split(";").map((s) => s.trim()).filter(Boolean);
    rows.push({
      primary_name: primary,
      former_names: listAt(1),
      nicknames: listAt(2),
      tag_teams: listAt(3),
      factions: listAt(4),
      era: (parts[5] || "").trim() || null,
      active: true,
    });
  });
  return { rows, errors };
}

export default function AdminBulkImport() {
  const nav = useNavigate();
  const [text, setText] = useState(SAMPLE);
  const [upsert, setUpsert] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const parsed = useMemo(() => parseSheet(text), [text]);

  const importNow = async () => {
    if (parsed.rows.length === 0) { toast.error("Nothing to import"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/wrestlers/bulk-import", {
        rows: parsed.rows, upsert,
      });
      setResult(data);
      toast.success(`Imported ${data.created} new, updated ${data.updated}`);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
      <button onClick={() => nav("/admin/wrestlers")}
        className="text-xs font-mono uppercase tracking-widest text-slate-400 hover:text-white mb-2 flex items-center gap-1"
        data-testid="bulk-back">
        <ArrowLeft className="h-3 w-3" /> Back to Wrestlers
      </button>
      <div className="text-xs font-mono uppercase tracking-widest text-red-500 mb-1">Roster · Bulk Import</div>
      <h1 className="font-display text-3xl sm:text-5xl font-black uppercase">Alias Import Sheet</h1>
      <p className="text-slate-400 text-sm mt-2 max-w-xl">
        Drop 100 wrestlers with aliases in one go. Each line becomes one wrestler.
        Fields are pipe-separated; aliases inside a field are semicolon-separated.
      </p>

      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs uppercase tracking-widest text-slate-400 font-mono">Paste sheet</Label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="bulk-textarea"
            className="mt-1.5 bg-slate-950 border-slate-700 font-mono text-xs min-h-[380px] leading-relaxed"
          />
        </div>
        <div className="space-y-4">
          <Card className="bg-slate-900/70 border-slate-800 p-4">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Preview</div>
            <div className="grid grid-cols-2 gap-2 text-center">
              <Metric label="Rows parsed" value={parsed.rows.length} testid="bulk-count-rows" />
              <Metric label="Errors" value={parsed.errors.length} testid="bulk-count-errors" color={parsed.errors.length ? "text-red-500" : "text-emerald-400"} />
            </div>
            {parsed.errors.length > 0 && (
              <ul className="mt-2 text-[10px] font-mono text-red-400 space-y-0.5 max-h-24 overflow-auto">
                {parsed.errors.map((e) => (
                  <li key={e.line}>Line {e.line}: {e.msg}</li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="bg-slate-900/70 border-slate-800 p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-display font-bold uppercase">Merge duplicates</div>
                <div className="text-[10px] font-mono uppercase text-slate-500">Same primary name → merge aliases</div>
              </div>
              <Switch checked={upsert} onCheckedChange={setUpsert} data-testid="bulk-upsert-toggle" />
            </div>
          </Card>

          <Button onClick={importNow} disabled={busy || parsed.rows.length === 0}
            data-testid="bulk-import-btn"
            className="w-full bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wide h-11">
            <Upload className="h-4 w-4 mr-2" /> {busy ? "Importing…" : `Import ${parsed.rows.length}`}
          </Button>

          {result && (
            <Card className="bg-slate-900/70 border-slate-800 p-4" data-testid="bulk-result">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Result</div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Metric label="New" value={result.created} color="text-emerald-400" testid="bulk-created" />
                <Metric label="Updated" value={result.updated} color="text-slate-200" testid="bulk-updated" />
                <Metric label="Skipped" value={result.skipped} color="text-amber-400" testid="bulk-skipped" />
              </div>
              {result.errors?.length > 0 && (
                <ul className="mt-2 text-[10px] font-mono text-red-400 space-y-0.5 max-h-24 overflow-auto">
                  {result.errors.map((e, i) => (
                    <li key={i} className="flex items-center gap-1">
                      <XCircle className="h-3 w-3" /> row {e.row}: {e.reason}
                    </li>
                  ))}
                </ul>
              )}
              <Button variant="outline" onClick={() => nav("/admin/wrestlers")}
                className="w-full mt-3 border-slate-700" data-testid="bulk-view-roster">
                <CheckCircle2 className="h-4 w-4 mr-2" /> View Roster
              </Button>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, color = "text-white", testid }) {
  return (
    <div>
      <div className={`font-mono text-2xl font-black ${color}`} data-testid={testid}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
    </div>
  );
}
